import React from 'react';
import { Trans } from 'react-i18next';
import { Button, Loader } from 'hfs-common-ui';
import { PortalModal } from '~/components/views/common/styled/Modal/Modal';
import { useGetAssetsBasicInformationQuery } from '~/api/assets-api/assets-api';
import RetryButton from '~/components/views/retry/RetryButton/RetryButton';
import type { ErrorType } from '~/data/ErrorRecord';
import { InternationalizationNameSpace } from '~/constants/internationalization-namespace';
import * as S from '~/domain/assets/components/OperationFailureIndicator/styled';

export interface Props {
  failedIprIds: string[];
  totalCount: number;
  dismissCallback: () => void;
  header?: React.ReactNode;
  message?: string;
  dismissButtonContent?: React.ReactNode;
}

export const FailedMowersListModal = ({
  totalCount,dismissCallback,
  failedIprIds,
  header,
  message,
  dismissButtonContent,
}: Props) => {
  const {
    isLoading,
    isFetching,
    data: assetsBasicInfoResponse,
    error: getAssetsBasicInfoError,
    refetch: refetchAssetsBasicInfo,
  } = useGetAssetsBasicInformationQuery(
    {
      iprIds: failedIprIds,
      attributes: ['name'],
    },
    {
      skip: failedIprIds.length === 0,
    }
  );
  const showSpinner = isFetching || isLoading;
  return (
    <PortalModal
      isOpen={true}
      hideHeader={false}
      header={
        header ?? (
          <S.ControlButtonCommandErrorTitleDiv $centerAlign={true}>
            <span>
              <Trans
                i18nKey="batch.feedback.failure.partial_failure_modal_title"
                ns={InternationalizationNameSpace.RoboticMowers}
                values={{ totalCount, failedCount: failedIprIds.length }}
              />
            </span>
          </S.ControlButtonCommandErrorTitleDiv>
        )
      }
      enforcedCloseOnBackdropClick={true}
      onClose={dismissCallback}
      infoOrErrorOrWarning={true}
      showCloseIcon={true}
    >
      {showSpinner && (
        <S.LoaderWrapperDiv>
          <Loader size="small" />
        </S.LoaderWrapperDiv>
      )}
      {!showSpinner && getAssetsBasicInfoError && (
        <RetryButton
          callback={refetchAssetsBasicInfo}
          errorMessage={(getAssetsBasicInfoError as ErrorType).description}
        />
      )}

      {!showSpinner && !getAssetsBasicInfoError && (
        <>
          {message && (
            <S.ModalParagraphDiv>
              <span>{message}</span>
            </S.ModalParagraphDiv>
          )}
          <S.ModalParagraphDiv>
            <Trans
              i18nKey="batch.feedback.failure.general_description"
              ns={InternationalizationNameSpace.RoboticMowers}
            />
          </S.ModalParagraphDiv>
          <S.ModalParagraphDiv>
            <S.ModalAssetsUl>
              {assetsBasicInfoResponse?.items?.map((asset) => (
                <li key={asset.iprId}>{asset.name}</li>
              ))}
            </S.ModalAssetsUl>
          </S.ModalParagraphDiv>
          <S.ModalParagraphDiv>
            <Trans
              i18nKey="batch.feedback.failure.general_recommendation"
              ns={InternationalizationNameSpace.RoboticMowers}
            />
          </S.ModalParagraphDiv>
        </>
      )}
      <S.ControlButtonCommandErrorFooterDiv>
        {!showSpinner && !getAssetsBasicInfoError && (
          <Button variant="primary" onClick={dismissCallback}>
            {dismissButtonContent ?? (
              <Trans i18nKey="ok_button" ns={InternationalizationNameSpace.Common} />
            )}
          </Button>
        )}
      </S.ControlButtonCommandErrorFooterDiv>
    </PortalModal>
  );
};

export default FailedMowersListModal;
